/**
 * FEECM-17598: Custom JSON parser to preserve acatControlNumber precision
 *
 * Problem:
 * - Backend returns acatControlNumber as JSON number: 202605061002577944 (18 digits)
 * - JavaScript Number.MAX_SAFE_INTEGER is only 16 digits
 * - JSON.parse() loses precision: 202605061002577944 → 202605061002577950
 * - Type inconsistency: some control numbers < 17 digits stayed as numbers, causing strict equality (===) failures
 *
 * Solution:
 * - Use regex to convert ALL acatControlNumber fields from number to string BEFORE JSON.parse()
 * - This preserves precision for large numbers AND ensures type consistency for all control numbers
 * - URL params are always strings, so API responses must also be strings for comparisons to work
 *
 * Why field-specific regex (not generic):
 * - We explicitly typed acatControlNumber as string (FEECM-17598)
 * - Other numeric fields remain typed as number and should stay numbers
 * - Generic regex would break type contracts by converting all large numbers to strings
 *
 * Temporary workaround:
 * - Remove this when backend API is updated to return acatControlNumber as string
 * - Backend ticket: [TODO: Add JIRA ticket number]
 */

/**
 * Parse JSON with acatControlNumber precision preservation
 *
 * @param text - JSON string from API response
 * @returns Parsed object with acatControlNumber as string (preserving precision)
 */
export const parseJSON = (text: string) => {
  // If not a string, return as-is (axios may have already parsed it)
  if (typeof text !== 'string') {
    return text
  }

  try {
    // Replace acatControlNumber OR acatsControlNumber: number → string (ALL control numbers)
    // Pattern: "acatControlNumber": 20261340004621 OR "acatsControlNumber": 202605061002577944
    // Result:  "acatControlNumber":"20261340004621" OR "acatsControlNumber":"202605061002577944"
    // Convert ALL control numbers to strings (not just 17+ digits) for type consistency
    // Note: Search API uses "acatControlNumber", Details API uses "acatsControlNumber" (with 's')
    const fixed = text.replace(
      /"(acats?)ControlNumber":\s*(\d+)/g,
      '"$1ControlNumber":"$2"'
    )

    return JSON.parse(fixed)
  } catch (error) {
    console.error('JSON parse error for acatControlNumber precision fix:', error)
    // Fallback to standard JSON.parse if regex transformation fails
    // This ensures we don't break the app if the regex has issues
    return JSON.parse(text)
  }
}
